import React from 'react';
import { Box, Button } from '@mui/material';
import { Table, Tooltip } from 'antd';
import type { DrawerProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PopupTableRow, formatExactCurrency } from '../utils';
import { RevenueChurnedSummary } from './types';
import { CHURNED_PAGE_SIZE } from '../constants';

interface RevenueLegendProps {}

export const RevenueLegend: React.FC<RevenueLegendProps> = () => (
  <Box
    sx={{
      position: 'fixed',
      left: 16,
      bottom: 88,
      zIndex: 1000,
      background: '#ffffff',
      borderRadius: '10px',
      boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
      padding: '6px 10px',
      display: 'flex',
      gap: 2,
      alignItems: 'center',
      border: '1px solid #e9ecef',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <svg width="12" height="18" viewBox="0 0 384 512" style={{ display: 'inline-block' }}>
        <path fill="#2e7d32" stroke="white" strokeWidth="16" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
      </svg>
      <span style={{ color: '#2e7d32', fontWeight: 600, fontSize: 12 }}>High Revenue (&gt;$1M)</span>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <svg width="12" height="18" viewBox="0 0 384 512" style={{ display: 'inline-block' }}>
        <path fill="#1a73e8" stroke="white" strokeWidth="16" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
      </svg>
      <span style={{ color: '#1a73e8', fontWeight: 600, fontSize: 12 }}>Good Revenue ($500K–$1M)</span>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <svg width="12" height="18" viewBox="0 0 384 512" style={{ display: 'inline-block' }}>
        <path fill="#f29900" stroke="white" strokeWidth="16" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
      </svg>
      <span style={{ color: '#f29900', fontWeight: 600, fontSize: 12 }}>Emerging (&lt;$500K)</span>
    </Box>
  </Box>
);

interface RevenueDrawerExtrasProps {
  topClientRows: PopupTableRow[];
  churnedClients: PopupTableRow[];
  churnedSummary: RevenueChurnedSummary;
  onOpenTopClients: () => void;
  onOpenChurned: () => void;
}

export const RevenueDrawerExtras: React.FC<RevenueDrawerExtrasProps> = ({
  topClientRows,
  churnedClients,
  churnedSummary,
  onOpenTopClients,
  onOpenChurned,
}) => (
  <>
    {topClientRows.length > 0 && (
      <Box sx={{ mb: 2, p: 2, border: '1px solid #e9ecef', borderRadius: 1, background: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: '#222' }}>Top 10 Client Insights</Box>
            <Box sx={{ fontSize: 12, color: '#5f6368' }}>Highest revenue clients based on current filters.</Box>
          </Box>
          <Button variant="outlined" size="small" onClick={onOpenTopClients}>
            View Details
          </Button>
        </Box>
      </Box>
    )}
    {churnedClients.length > 0 && (
      <Box sx={{ mb: 2, p: 2, border: '1px solid #e9ecef', borderRadius: 1, background: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: '#222' }}>Churned Clients ({churnedClients.length})</Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: 12, color: '#5f6368' }}>
              <span>Before Open SO: {churnedSummary.before}</span>
              <span>After Open SO: {churnedSummary.after}</span>
              <span>Conversion: {churnedSummary.conversionRate !== null ? `${churnedSummary.conversionRate.toFixed(0)}%` : '-'}</span>
            </Box>
          </Box>
          <Button variant="outlined" size="small" onClick={onOpenChurned}>
            View Details
          </Button>
        </Box>
      </Box>
    )}
  </>
);

interface RevenueTopClientsContentProps {
  rows: PopupTableRow[];
  formatCurrency: (value: number) => string;
  parseChurnAmount: (value?: string | number) => number;
  getRevenueTierColor: (value: number) => string;
  totalRevenue: number;
  onClientSelect?: (row: PopupTableRow) => void;
}

export const RevenueTopClientsContent: React.FC<RevenueTopClientsContentProps> = ({
  rows,
  formatCurrency,
  parseChurnAmount,
  getRevenueTierColor,
  totalRevenue,
  onClientSelect,
}) => {
  if (rows.length === 0) {
    return <Box sx={{ color: '#5f6368' }}>No client insights available.</Box>;
  }

  type TopClientTableRow = PopupTableRow & {
    key: string;
    rank: number;
    amount: number;
    percentage: number;
    color: string;
  };

  const dataSource: TopClientTableRow[] = rows.map((row, index) => {
    const amount = parseChurnAmount(row.total_amount);
    return {
      ...row,
      key: row.userid || row.location_id || `${row.name}-${index}`,
      rank: index + 1,
      amount,
      percentage: totalRevenue > 0 ? (amount / totalRevenue) * 100 : 0,
      color: getRevenueTierColor(amount),
    };
  });

  const columns: ColumnsType<TopClientTableRow> = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 64,
      render: (value: number) => <span style={{ color: '#6b7280', fontWeight: 600 }}>{value}</span>,
    },
    {
      title: 'Client',
      dataIndex: 'name',
      key: 'name',
      render: (_value, record) => {
        const handleLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          if (record.userid && onClientSelect) {
            onClientSelect(record);
          }
        };

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <svg width="12" height="18" viewBox="0 0 384 512" style={{ display: 'inline-block', flexShrink: 0 }}>
              <path
                fill={record.color}
                stroke="white"
                strokeWidth="16"
                d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"
              />
            </svg>
            <span
              style={{ fontSize: 13, fontWeight: 600, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={record.name}
            >
              {record.userid ? (
                <a href="#" onClick={handleLink} style={{ color: 'rgb(0, 145, 5)', textDecoration: 'underline', cursor: 'pointer' }}>
                  {record.name}
                </a>
              ) : (
                record.name
              )}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Sales Rep',
      dataIndex: 'staff',
      key: 'staff',
      render: (value: PopupTableRow['staff']) => <span style={{ fontSize: 12, color: '#6b7280' }}>{value || '-'}</span>,
    },
    {
      title: 'Location',
      key: 'location',
      render: (_value, record) => (
        <span style={{ fontSize: 12, color: '#5f6368', whiteSpace: 'nowrap' }}>{(record.city ? `${record.city}, ` : '') + (record.state || '')}</span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (value: number) => (
        <Tooltip title={formatExactCurrency(value)}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{formatCurrency(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Revenue %',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (value: number, record) => (
        <span style={{ fontSize: 12, fontWeight: 700 }}>{record.amount > 0 && totalRevenue > 0 ? `${value.toFixed(2)}%` : '-'}</span>
      ),
    },
  ];

  return (
    <Table<TopClientTableRow>
      size="middle"
      rowKey={(record) => record.key}
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );
};

interface RevenueChurnedContentProps {
  rows: PopupTableRow[];
  paginatedRows: PopupTableRow[];
  churnedTotalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  formatCurrency: (value: number) => string;
  parseChurnAmount: (value?: string | number) => number;
  parseInvoiceDate: (value?: string) => Date | null;
  formatInvoiceDate: (value: Date) => string;
  formatRelativeDate: (value: Date) => string;
  computeOpenSOMetrics: (openSo: string | undefined, invoiceDate: Date | null) => { before: number; after: number; conversionRate: number | null };
  totalRevenue: number;
  onClientSelect?: (row: PopupTableRow) => void;
}

export const RevenueChurnedContent: React.FC<RevenueChurnedContentProps> = ({
  rows,
  paginatedRows,
  churnedTotalPages,
  currentPage,
  onPageChange,
  formatCurrency,
  parseChurnAmount,
  parseInvoiceDate,
  formatInvoiceDate,
  formatRelativeDate,
  computeOpenSOMetrics,
  totalRevenue,
  onClientSelect,
}) => {
  if (rows.length === 0) {
    return <Box sx={{ color: '#5f6368' }}>No churned clients found.</Box>;
  }

  type ChurnedTableRow = PopupTableRow & {
    key: string;
    invoiceDate: Date | null;
    invoiceRelative: string;
    invoiceTooltip?: string;
    beforeMetric: number;
    afterMetric: number;
    conversionLabel: string;
    amount: number;
    percentage: number;
  };

  const dataSource: ChurnedTableRow[] = paginatedRows.map((row, index) => {
    const invoiceDate = parseInvoiceDate(row.invoice_created_date);
    const { before, after, conversionRate } = computeOpenSOMetrics(row.open_so, invoiceDate);
    const amount = parseChurnAmount(row.total_amount);

    return {
      ...row,
      key: row.userid || row.location_id || `${row.name}-${currentPage}-${index}`,
      invoiceDate,
      invoiceRelative: invoiceDate ? formatRelativeDate(invoiceDate) : '-',
      invoiceTooltip: invoiceDate ? formatInvoiceDate(invoiceDate) : undefined,
      beforeMetric: before,
      afterMetric: after,
      conversionLabel: conversionRate !== null ? `${conversionRate.toFixed(0)}%` : '-',
      amount,
      percentage: totalRevenue > 0 ? (amount / totalRevenue) * 100 : 0,
    };
  });

  const columns: ColumnsType<ChurnedTableRow> = [
    {
      title: 'Client',
      dataIndex: 'name',
      key: 'name',
      render: (_value, record) => {
        const handleLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          if (record.userid && onClientSelect) {
            onClientSelect(record);
          }
        };

        return (
          <span
            style={{ fontSize: 13, fontWeight: 600, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={record.name}
          >
            {record.userid ? (
              <a href="#" onClick={handleLink} style={{ color: 'rgb(0, 145, 5)', textDecoration: 'underline', cursor: 'pointer' }}>
                {record.name}
              </a>
            ) : (
              record.name
            )}
          </span>
        );
      },
    },
    {
      title: 'Sales Rep',
      dataIndex: 'staff',
      key: 'staff',
      render: (value: PopupTableRow['staff']) => <span style={{ fontSize: 12, color: '#6b7280' }}>{value || '-'}</span>,
    },
    {
      title: 'Location',
      key: 'location',
      render: (_value, record) => (
        <span style={{ fontSize: 12, color: '#5f6368' }}>{(record.city ? `${record.city}, ` : '') + (record.state || '')}</span>
      ),
    },
    {
      title: 'Last Invoice',
      dataIndex: 'invoiceRelative',
      key: 'invoiceRelative',
      render: (_value, record) =>
        record.invoiceDate ? (
          <Tooltip title={record.invoiceTooltip}>
            <span style={{ fontSize: 12, color: '#c62828', fontWeight: 700 }}>{record.invoiceRelative}</span>
          </Tooltip>
        ) : (
          <span style={{ fontSize: 12, color: '#6b7280' }}>-</span>
        ),
    },
    {
      title: 'B/F Open SO',
      dataIndex: 'beforeMetric',
      key: 'beforeMetric',
      render: (value: number) => <span style={{ fontSize: 12, color: '#5f6368' }}>{value}</span>,
    },
    {
      title: 'A/F Open SO',
      dataIndex: 'afterMetric',
      key: 'afterMetric',
      render: (value: number) => <span style={{ fontSize: 12, color: '#5f6368' }}>{value}</span>,
    },
    {
      title: 'Conversion Rate',
      dataIndex: 'conversionLabel',
      key: 'conversionLabel',
      render: (value: string) => <span style={{ fontSize: 12, color: '#5f6368' }}>{value}</span>,
    },
    {
      title: 'Total',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (value: number) => (
        <Tooltip title={formatExactCurrency(value)}>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{formatCurrency(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Revenue %',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (value: number, record) => (
        <span style={{ fontSize: 12, fontWeight: 700 }}>{record.amount > 0 && totalRevenue > 0 ? `${value.toFixed(2)}%` : '-'}</span>
      ),
    },
  ];

  return (
    <Table<ChurnedTableRow>
      size="middle"
      rowKey={(record) => record.key}
      dataSource={dataSource}
      columns={columns}
      pagination={{
        current: currentPage,
        pageSize: CHURNED_PAGE_SIZE,
        total: rows.length,
        onChange: onPageChange,
        showSizeChanger: false,
        hideOnSinglePage: churnedTotalPages <= 1,
      }}
      scroll={{ x: 'max-content' }}
    />
  );
};

export type RevenueDrawerProps = Pick<DrawerProps, 'title' | 'placement' | 'width' | 'open' | 'onClose' | 'destroyOnClose'>;
